import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Chip,
    Alert,
    Tooltip,
    Button,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    Skeleton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ApiIcon from '@mui/icons-material/Api';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import {
    buildSchemaV32,
    buildSchemaV30,
    buildSchemaV28Base,
    SectionDef,
    FieldDef,
    useSchemaState,
    SchemaPanel,
} from './_shared';

// ─── Constants ─────────────────────────────────────────────────────────────────

const SPEC_FILE = '/carboncare-rest-calc-openapi.yaml';
// Calc "Try it out" → XML-REST-API calc backend (<host>/rest), per-env, not the spec host.
const REST_CALC_API_BASE =
    `${(process.env.REACT_APP_XML_API_URL || 'http://localhost:8080/xml/calc').replace('/xml/calc', '')}/rest`;
const SWAGGER_PAGE = `/swagger-ui.html?spec=${SPEC_FILE}&apiBase=${encodeURIComponent(REST_CALC_API_BASE)}`;

// ─── v2.8 schema (ApiReferenceTab variant) ────────────────────────────────────
// Uses the shared base (root → ferry + address types) and appends an API-specific
// response section that documents the XSD response element using original field names.

const V28_RESPONSE_FIELDS: FieldDef[] = [
    { name: 'Emmissions → CO2', type: 'decimal', required: true, description: 'CO2 emissions result in kg. Tank-to-wheel (direct).', notes: 'Note the typo "Emmissions" (double m) is in the original XSD.' },
    { name: 'Emmissions → CO2E', type: 'decimal', required: true, description: 'CO2-equivalent emissions in kg. Includes other greenhouse gas contributions (e.g. N₂O, CH₄).' },
    { name: 'Emmissions → CO2Wtw', type: 'decimal', required: true, description: 'CO2 Well-to-Wheel emissions in kg. Includes upstream fuel production emissions.' },
    { name: 'CompensationCosts', type: 'decimal', required: true, description: 'Cost of offsetting the emissions. Currency indicated by the Currency attribute (CHF, EUR, or USD).' },
    { name: 'Distance', type: 'decimal', required: true, description: 'Calculated route distance in km.' },
    { name: 'Status → IsError', type: 'boolean', required: true, description: 'Indicates if an error occurred during calculation.' },
    { name: 'Status → ErrorLeg', type: 'integer', required: true, description: 'The leg index where an error occurred. 0 if no error.' },
    { name: 'Status → ErrorCode', type: 'integer', required: true, description: 'Error code. 0 if no error. Codes remain stable across releases.' },
    { name: 'Status → ErrorMessage', type: 'string', required: false, description: 'Human-readable error message. May change between releases; use ErrorCode for programmatic handling.' },
    { name: 'DbId', type: 'integer', required: false, description: 'Internal database record ID assigned to the shipment result.' },
];

function buildSchemaV28Api(): SectionDef[] {
    const base = buildSchemaV28Base();
    // base ends with 4 address-type entries; insert response section before them
    const addressEntries = base.slice(-4);
    const transportEntries = base.slice(0, -4);
    return [
        ...transportEntries,
        {
            id: 'response', title: 'Response (Output Schema)', color: '#6d28d9',
            subtitle: 'v2.8 uniquely documents the API response structure in the XSD. This shows what fields are returned per shipment.',
            fields: V28_RESPONSE_FIELDS,
        },
        ...addressEntries,
    ];
}

const SCHEMAS: Record<string, SectionDef[]> = {
    '3.2': buildSchemaV32(),
    '3.0': buildSchemaV30(),
};

// ─── Main Component ───────────────────────────────────────────────────────────

type ViewMode = 'schema' | 'swagger';

export interface MetadataChip {
    label: string;
    color?: 'success' | 'primary' | 'default' | 'warning';
    variant?: 'outlined' | 'filled';
}

export interface ApiReferenceTabProps {
    specFile?: string;
    swaggerIframeSrc?: string;
    metadataChips?: MetadataChip[];
    schemaContent?: React.ReactNode;
    downloadFileName?: string;
    /** Logged-in licensee's API key — auto-injected into Swagger "Try it out". */
    apiKey?: string;
}

const ApiReferenceTab: React.FC<ApiReferenceTabProps> = ({
    specFile = SPEC_FILE,
    swaggerIframeSrc = SWAGGER_PAGE,
    metadataChips,
    schemaContent,
    downloadFileName = 'carboncare-rest-calc-openapi.yaml',
    apiKey = '',
}) => {
    const [view, setView] = useState<ViewMode>('schema');

    // Swagger iframe state
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Push the licensee API key into the embedded Swagger UI so "Try it out" is
    // pre-authorized (no manual Authorize, no 401). Sent via postMessage — never
    // in the iframe URL — so the key can't leak into history or logs.
    const postAuth = useCallback(() => {
        const win = iframeRef.current?.contentWindow;
        if (win && apiKey) {
            win.postMessage({ type: 'cc-swagger-auth', key: apiKey }, window.location.origin);
        }
    }, [apiKey]);

    // The iframe pings "ready" once Swagger is built; respond with the key.
    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            if (e.origin === window.location.origin && (e.data as { type?: string })?.type === 'cc-swagger-ready') {
                postAuth();
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [postAuth]);

    // Re-send when the key resolves after the iframe is already up.
    useEffect(() => { postAuth(); }, [postAuth]);

    const schemaState = useSchemaState(SCHEMAS);

    const handleDownload = () => {
        const a = document.createElement('a');
        a.href = specFile;
        a.download = downloadFileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handleOpenInNew = () => {
        globalThis.open(swaggerIframeSrc, '_blank', 'noopener,noreferrer');
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* ── Persistent toolbar (always visible) ── */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    bgcolor: '#f8faf8',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                }}
            >
                {/* Metadata chips */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {metadataChips
                        ? metadataChips.map((chip) => (
                            <Chip
                                key={chip.label}
                                label={chip.label}
                                size="small"
                                color={chip.color ?? 'default'}
                                variant={chip.variant ?? 'outlined'}
                            />
                        ))
                        : <>
                            <Chip label="OpenAPI 3.0.3" size="small" color="success" variant="outlined" />
                            <Chip label="REST / JSON" size="small" variant="outlined" />
                            <Chip label="POST /rest/calc" size="small" color="primary" variant="outlined" />
                        </>
                    }
                </Stack>

                <Box sx={{ flex: 1 }} />

                {/* View toggle */}
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, val) => { if (val) setView(val); }}
                    size="small"
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.5, fontSize: '0.8rem' } }}
                >
                    <ToggleButton value="schema">
                        <SchemaOutlinedIcon sx={{ fontSize: 15, mr: 0.6 }} />
                        Schema Reference
                    </ToggleButton>
                    <ToggleButton value="swagger">
                        <ApiIcon sx={{ fontSize: 15, mr: 0.6 }} />
                        Swagger UI
                    </ToggleButton>
                </ToggleButtonGroup>

                <Tooltip title="Download the OpenAPI 3.0.3 YAML specification file">
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        color="success"
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Download spec (.yaml)
                    </Button>
                </Tooltip>
            </Box>

            {/* ── Swagger view ── */}
            {view === 'swagger' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Auth info banner + Open full screen button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Alert
                            icon={<InfoOutlinedIcon fontSize="small" />}
                            severity="info"
                            variant="outlined"
                            sx={{ py: 0.5, flex: 1 }}
                        >
                            <Typography variant="body2">
                                {apiKey ? (
                                    <>
                                        Your licensee <strong>CarbonCare API key</strong> is pre-loaded as the{' '}
                                        <code style={{ background: '#e3f2fd', padding: '1px 4px', borderRadius: 3 }}>
                                            Bearer
                                        </code>{' '}
                                        token — just expand an operation, click <strong>Try it out</strong>, then{' '}
                                        <strong>Execute</strong>. Use <strong>Authorize</strong> to override it.
                                    </>
                                ) : (
                                    <>
                                        Authenticate using your <strong>CarbonCare API key</strong> as a{' '}
                                        <code style={{ background: '#e3f2fd', padding: '1px 4px', borderRadius: 3 }}>
                                            Bearer
                                        </code>{' '}
                                        token in the{' '}
                                        <code style={{ background: '#e3f2fd', padding: '1px 4px', borderRadius: 3 }}>
                                            Authorization
                                        </code>{' '}
                                        header. Click <strong>Authorize</strong> in the Swagger panel below to set it.
                                    </>
                                )}
                            </Typography>
                        </Alert>
                        <Tooltip title="Open Swagger UI in a new browser tab">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={handleOpenInNew}
                                sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                Open full screen
                            </Button>
                        </Tooltip>
                    </Box>

                    {/* Swagger iframe */}
                    <Box
                        sx={{
                            position: 'relative',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: '#fff',
                            minHeight: 700,
                        }}
                    >
                        {!iframeLoaded && !iframeError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: '#fff',
                                    zIndex: 1,
                                    p: 3,
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Swagger header strip */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Skeleton variant="rounded" width={48} height={48} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width="38%" sx={{ fontSize: '1.5rem' }} />
                                        <Skeleton variant="text" width="22%" sx={{ fontSize: '0.875rem' }} />
                                    </Box>
                                    <Skeleton variant="rounded" width={140} height={36} />
                                </Box>
                                {/* Operation rows */}
                                {Array.from({ length: 7 }, (_, i) => `swagger-op-skeleton-${i}`).map((rowKey, idx) => (
                                    <Box
                                        key={rowKey}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            py: 1.25,
                                            borderTop: '1px solid rgba(0,0,0,0.06)',
                                        }}
                                    >
                                        <Skeleton variant="rounded" width={62} height={24} sx={{ borderRadius: '4px' }} />
                                        <Skeleton variant="text" width={`${30 + ((idx * 7) % 35)}%`} sx={{ fontSize: '0.95rem' }} />
                                        <Box sx={{ flex: 1 }} />
                                        <Skeleton variant="circular" width={20} height={20} />
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {iframeError && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    p: 4,
                                }}
                            >
                                <Alert severity="warning" sx={{ maxWidth: 480 }}>
                                    Could not load the embedded Swagger UI. Try opening it in a new tab
                                    using the button above, or download the spec file directly.
                                </Alert>
                            </Box>
                        )}

                        <iframe
                            ref={iframeRef}
                            src={swaggerIframeSrc}
                            title="CarbonCare API — Swagger UI"
                            style={{
                                width: '100%',
                                height: '820px',
                                border: 'none',
                                display: iframeLoaded && !iframeError ? 'block' : 'none',
                            }}
                            onLoad={() => { setIframeLoaded(true); postAuth(); }}
                            onError={() => { setIframeLoaded(true); setIframeError(true); }}
                        />
                    </Box>
                </Box>
            )}

            {/* ── Schema Reference view ── */}
            {view === 'schema' && schemaContent && (
                <Box sx={{ width: '100%' }}>
                    {schemaContent}
                </Box>
            )}

            {view === 'schema' && !schemaContent && (
                <Box sx={{ width: '100%' }}>
                    <SchemaPanel state={schemaState} />
                </Box>
            )}
        </Box>
    );
};

export default ApiReferenceTab;
